import { useMemo } from "react";
import {
  EmploymentCategoryDescriptionCode,
  IPartialAPISPartyOption,
  IPartialAPISParty,
  IRider,
  IFullFlight,
} from "@vmosolutions/oc-interfaces";
//formik
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
//material UI
import { Button, Grid, Typography, styled, alpha } from "@mui/material";
//Yup Validation
import * as Yup from "yup";
// API calls
import { useDispatch } from "react-redux";

import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { addSelectedFlight } from "src/redux/actions/flights/FlightData.Actions";

const employmentCategoryOptions = Object.keys(EmploymentCategoryDescriptionCode).map((key) => ({
  label: key,
  value: EmploymentCategoryDescriptionCode[key as keyof typeof EmploymentCategoryDescriptionCode],
}));

interface IRidersFormProps {
  selectedRider?: IPartialAPISParty | null;
  className?: string;
  updateRiderList?: (Rider: IRider, mode: "create" | "update" | "delete") => void;
  ridersOptions?: IPartialAPISPartyOption[];
  ridersOnFlight: IRider[];
  flightId: string;
  windowId: string;
  setLoadedFlight: (flightValues: IFullFlight) => void;
  // setActiveModal?: (activeModal: IActiveModal) => void;
}
//set initial value when user click "new Rider"
const initialRiderValues: IRider = {
  apisparty_id: null, //tried with "" or undefined and still having the same issue
  APISEmploymentCategory: EmploymentCategoryDescriptionCode.CargoNonCockpitCrew,
};

type RiderForm = FormikProps<IRider>;

// Rider Form Validation with YUP
const RiderSchema = Yup.object().shape({
  apisparty_id: Yup.string().nullable().required("Rider is a required"),
  APISEmploymentCategory: Yup.string()
    .oneOf(Object.values(EmploymentCategoryDescriptionCode))
    .nullable()
    .required(" Employment Type is required"),
});

const OCRidersForm = styled(Form)(({ theme }) => ({
  "& .pairing_info_accordion": {
    "&::before": { display: "none" },
    "&.Mui-expanded": { margin: 0 },
    color: theme.typography.body1.color,
    background: theme.custom.timeline.contextListBackground,
    boxShadow: "none",
    "& .pairing_info_summary": {
      minHeight: 30,
      "& .MuiAccordionSummary-content": {
        margin: "6px 0px",
      },
      "& .MuiAccordionSummary-expandIcon": {
        padding: 6,
      },
      "& .crew_position_display": {
        color: theme.typography.body2.color,
        backgroundColor: "blue",
      },
    },
  },

  "& .form_header": {
    display: "flex",
    justifyContent: "space-between",
    margin: "0px 5px",
    "& .title": { margin: "0px 10px" },
  },

  "& .form_body": {
    "& .expansion_panel": {
      borderRadius: 0,
      backgroundColor: "transparent",
      boxShadow: "none",
      "&:before": { display: "none" },

      "&.Mui-expanded": {
        margin: 5,
        border: `1px solid ${alpha(theme.custom.forms.inputText, 0.1)}`,
        "& .expansion_summary": {
          backgroundColor: alpha(theme.custom.forms.inputText, 0.1),
        },
      },

      "& .expansion_focused": {
        backgroundColor: "transparent",
      },

      "& .expansion_summary": {
        padding: 0,
        minHeight: "auto",
        justifyContent: "flex-start",
        cursor: "default",
        "& .MuiAccordionSummary-content": {
          margin: 0,
          flexGrow: 1,
          alignItems: "center",
        },
        "& .edit_mode_button, & .create_mode_button": {
          padding: 0,
          margin: "2px 5px",
          border: `1px solid ${theme.custom.forms.pristineColor}`,
          borderRadius: 3,
          width: 22,
          height: 22,
          "&.Mui-disabled": { opacity: 0.5 },
          "& svg": {
            color: theme.custom.forms.pristineColor,
            width: 18,
            height: 18,
          },
          "&:hover": {
            border: `1px solid ${theme.custom.forms.focusedColor}`,
            "& svg": { color: theme.custom.forms.focusedColor },
          },
        },
      },
      "& .expansion_details": {
        padding: 0,
        flexDirection: "column",
      },
    },
  },
  "& .form_footer": {
    marginTop: 10,
    display: "flex",
    justifyContent: "flex-start",
    "& button": { margin: "5px 5px" },
    "& .btn": {
      flexWrap: "wrap",
      display: "flex",
      height: 27,
      fontSize: 10,
      lineHeight: "12px",
      padding: "2px 8px",
      margin: "0px 4px",
      "&.submit": {
        color: theme.custom.forms.inputBack,
      },
    },
  },
  "& .dialog_spinner_progress": { margin: -10 },
  "& .field-group": {
    flexDirection: "row",
    width: 380,
  },
  "& p.required_fields_label": {
    margin: 0,
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& .required_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
    padding: 10,
    width: "100%",
  },
  "& .optional_fields": {
    margin: "2px 0px",
    padding: 10,
  },
  // STYLE FIELDS (includes individual field settings)
  "& .field": {
    width: "auto",
    minWidth: 238,
    "&.textfield": {
      "&.revision": {
        width: 260,
        marginRight: 15,
        "& .input_control": {
          "& label": { width: 150 },
          "& .input-base": { width: 80 },
        },
      },
      "&.multiline_short": {
        width: 350,
        "& .input_control": {
          "& label": { width: "auto" },
        },
      },
      "&.multiline_long": {
        width: "100%",
        maxWidth: 540,
        "& .input_control": {
          "& label": { width: 150 },
        },
      },
      "&.address": {
        width: 238,
        maxWidth: 540,
        "& .input_control": {},
      },
    },
    "&.selectfield": {
      "&.aircraft_type": { minWidth: 300 },
    },
  },
  "& .grid_item_divider": {
    marginLeft: 20,
    borderLeft: `1px solid ${theme.custom.forms.labels}`,
  },
}));

/**
 * Base Rider Form
 * @param props - selected Rider (from ManageRider)
 */
export const RidersForm = (props: IRidersFormProps): JSX.Element => {
  const {
    selectedRider,
    className,
    updateRiderList,
    ridersOptions,
    ridersOnFlight,
    flightId,
    windowId,
    setLoadedFlight,
  } = props;
  const dispatch = useDispatch();

  //should be later moved to client setting
  const disableSelectingEmpType = true;
  const mosaicPane = useMemo(() => {
    return windowId ? document.getElementById(`${windowId}-panel`) : null;
  }, [windowId]);

  const filteredRiderOptions = useMemo(() => {
    if (ridersOptions) {
      return !ridersOnFlight
        ? ridersOptions.filter((option) => !option.markedForDeletion)
        : ridersOptions.filter((option) => {
            const isAlreadyAssociated = ridersOnFlight.find((rider: IRider) => rider.apisparty_id === option._id);
            return isAlreadyAssociated ? null : option.markedForDeletion ? null : option;
          });
    } else {
      return [];
    }
  }, [ridersOnFlight, ridersOptions]);

  const initialValues: IRider = useMemo(
    () =>
      selectedRider
        ? ({
            apisparty_id: selectedRider._id,
            APISEmploymentCategory: selectedRider.APISEmploymentCategory,
          } as IRider)
        : initialRiderValues,
    [selectedRider]
  );

  const handleSubmit = async (values: IRider, helpers: FormikHelpers<IRider>): Promise<void> => {
    const { setSubmitting, setStatus, resetForm } = helpers;

    const index = ridersOnFlight?.findIndex((rider: IRider) => values.apisparty_id === rider.apisparty_id);
    const mode = index > -1 ? "update" : "create";
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: `${mode === "update" ? "Updating" : "Adding New"} Rider...`,
      successMessage: `Rider Successfully ${mode === "update" ? "Updated" : "Created"}.`,
      errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Rider`,
    };
    const updatedRiders: IRider[] = [...ridersOnFlight];

    if (mode === "create") {
      updatedRiders.push(values);
      // updateRiderList(values, "create");
    } else {
      updatedRiders[index] = values;
      // updateRiderList(values, "update");
    }

    const response = await AmplifyAPIWrapper.Put<any>(`/flights/${flightId}`, { riders: updatedRiders }, notifications);
    if (response?.newEvent?._id) {
      resetForm({ values });
      setStatus({ success: true });
      // add or update Rider from main list.
      updateRiderList(values, mode);
      //update form
      dispatch(addSelectedFlight(flightId));
      // update loaded flight in flight editor
      setLoadedFlight(response?.newEvent);
    } else {
      setStatus({ success: false });
    }
    setSubmitting(false);
  };

  return (
    <Formik
      className={className}
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={RiderSchema}
      // validateOnChange={false}
      validateOnBlur={false} //added this to stop the required message appearing on initial selection of rider
    >
      {(riderForm: RiderForm) => {
        const { values, dirty, isValid, isSubmitting, handleReset } = riderForm;

        const selectedRiderMatch = !!selectedRider && values?.apisparty_id === selectedRider._id;

        const handleDelete = async (): Promise<void> => {
          const index = ridersOnFlight?.findIndex((rider: IRider) => selectedRider?._id === rider.apisparty_id);
          const updatedRiders: IRider[] = [...ridersOnFlight];
          if (index > -1) {
            updatedRiders?.splice(index, 1);
          }

          const notifications: INotifications = {
            dispatch: dispatch,
            infoMessage: "Removing Rider...",
            successMessage: "Rider Successfully Removed.",
            errorMessage: "Error: Unable to Remove Rider",
          };

          const response = await AmplifyAPIWrapper.Put<any>(
            `/flights/${flightId}`,
            { riders: updatedRiders },
            notifications
          );
          if (response?.newEvent?._id) {
            riderForm.resetForm({ values: initialRiderValues });
            riderForm.setStatus({ success: true });
            // remove Rider from main list.
            updateRiderList(ridersOnFlight[index], "delete");
            // update loaded flight in flight editor
            setLoadedFlight(response?.newEvent);
          } else {
            riderForm.setStatus({ success: false });
          }
          riderForm.setSubmitting(false);
        };

        const handleInputChange = (event: any): void => {
          const target = event;
          const selectedRider: IPartialAPISParty = ridersOptions?.find(
            (option: IPartialAPISParty) => option?._id === target
          );
          const empType =
            selectedRider.APISEmploymentCategory === EmploymentCategoryDescriptionCode.CargoNonCockpitCrew
              ? EmploymentCategoryDescriptionCode.CargoNonCockpitCrew
              : EmploymentCategoryDescriptionCode.PilotsOnAircraftNotOnDuty;
          riderForm.setFieldValue("APISEmploymentCategory", empType, false);
        };

        return (
          <OCRidersForm>
            <Grid container className="form_body">
              <Typography className="required_fields_label">required fields</Typography>
              <Grid container direction="row" className="required_fields">
                <FormikSelectField
                  className="field selectfield"
                  name="apisparty_id"
                  label="Rider"
                  options={filteredRiderOptions}
                  parse={(option: any) => option._id}
                  getOptionValue={(option: IPartialAPISPartyOption) => option._id}
                  getOptionLabel={(rider: IPartialAPISPartyOption) =>
                    `${rider?.lastName}, ${rider?.firstName}${rider?.employeeId ? ` (${rider.employeeId})` : ""}`
                  }
                  format={(riderAPISId: string) =>
                    ridersOptions?.find((option: IPartialAPISPartyOption) => option?._id === riderAPISId) ?? null
                  }
                  onChange={handleInputChange}
                  disabled={selectedRiderMatch}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  isSearchable={true}
                  isClearable={false}
                  menuPortalTarget={mosaicPane}
                  menuPosition="fixed"
                />
                <FormikSelectField
                  className="field selectfield customer_type"
                  name="APISEmploymentCategory"
                  label="Employment Type"
                  options={employmentCategoryOptions}
                  parse={(option: any) => option?.value}
                  getOptionLabel={(option: any) => `${option?.value} - ${option?.label}`}
                  format={(empCat: string) =>
                    employmentCategoryOptions?.find((option: any) => option?.value === empCat) ?? null
                  }
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  isSearchable={true}
                  isClearable={false}
                  menuPortalTarget={mosaicPane}
                  menuPosition="fixed"
                  disabled={disableSelectingEmpType}
                />
              </Grid>

              <Grid container className="form_footer">
                <Button
                  className="btn reset"
                  type="reset"
                  variant="contained"
                  color="secondary"
                  disabled={(!dirty && isValid) || isSubmitting}
                  onClick={handleReset}
                >
                  Reset
                </Button>
                <Button
                  className={"btn save"}
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !isValid || !dirty || selectedRider?.markedForDeletion}
                  type="submit"
                >
                  {selectedRider ? "Modify" : "Add"}
                </Button>
                {selectedRider && (
                  <Button
                    className={"btn delete"}
                    variant="contained"
                    color="secondary"
                    disabled={!selectedRider || isSubmitting}
                    onClick={handleDelete}
                  >
                    Remove
                  </Button>
                )}
              </Grid>
            </Grid>
          </OCRidersForm>
        );
      }}
    </Formik>
  );
};
